/**
 * Lightweight WebSocket client for CITY RUN + Prophets Hub realtime updates.
 * Auto-reconnects with exponential backoff, exposes subscribe(callback).
 *
 * Server-side identity is derived from the httpOnly `hub_session` cookie
 * (attached automatically). For admin dashboards, the frontend must send
 * an in-band `auth.admin` message with the admin bearer token — browsers
 * cannot set custom WS headers, so this is the standard bootstrap path.
 * `authenticateAdmin(token)` and `resetIdentity()` are re-fired on every
 * reconnect so the correct room is re-joined transparently.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wsUrl() {
  const u = new URL(BACKEND_URL);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}/api/ws`;
}

class RaceSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Set();
    this.retry = 0;
    this.closed = false;
    // On-connect messages that must be replayed after every reconnect
    // (admin auth upgrade, identity reset). Kept as a small ordered queue.
    this._bootstrap = [];
  }

  connect() {
    this.closed = false;
    try {
      this.ws = new WebSocket(wsUrl());
    } catch (e) {
      this._scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.retry = 0;
      // Replay bootstrap messages so admin identity survives reconnect.
      for (const m of this._bootstrap) {
        try { this.ws?.send(JSON.stringify(m)); } catch { /* ignore */ }
      }
    };
    this.ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        this.listeners.forEach((cb) => cb(data));
      } catch { /* ignore */ }
    };
    this.ws.onclose = () => {
      this.ws = null;
      if (!this.closed) this._scheduleReconnect();
    };
    this.ws.onerror = () => {
      try { this.ws?.close(); } catch { /* noop */ }
    };
  }

  _scheduleReconnect() {
    const delay = Math.min(15000, 500 * 2 ** this.retry);
    this.retry += 1;
    setTimeout(() => this.connect(), delay);
  }

  subscribe(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  // Send once now if the socket is open; always queue for future reconnects.
  _sendAndPersist(msg) {
    this._bootstrap = this._bootstrap.filter((m) => m.type !== msg.type);
    this._bootstrap.push(msg);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(msg)); } catch { /* noop */ }
    }
  }

  /**
   * Upgrade this socket to an admin-scoped identity. Server verifies the
   * bearer token against its own JWT secret; token from a non-admin is
   * rejected server-side. Replayed on every reconnect.
   */
  authenticateAdmin(token) {
    if (!token) return;
    this._sendAndPersist({ type: "auth.admin", token });
  }

  /**
   * Drop server-side identity for this connection. Fired on logout so a
   * reconnect after a different user's login doesn't inherit the old
   * user's user-scoped events.
   */
  resetIdentity() {
    // Also drop the queued admin auth so a re-open doesn't rejoin the room.
    this._bootstrap = this._bootstrap.filter((m) => m.type !== "auth.admin");
    this._sendAndPersist({ type: "auth.reset" });
  }

  /**
   * Force-close and re-open the underlying WebSocket so it re-picks up the
   * current auth cookies. Call this after login/logout for a clean identity
   * pivot rather than relying only on `resetIdentity()`.
   */
  reconnect() {
    try { this.ws?.close(); } catch { /* noop */ }
    // onclose will schedule the reconnect; nothing else to do here.
  }

  close() {
    this.closed = true;
    try { this.ws?.close(); } catch { /* noop */ }
  }
}

let singleton = null;
export function getSocket() {
  if (!singleton) {
    singleton = new RaceSocket();
    singleton.connect();
  }
  return singleton;
}
